import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUserStore } from '../../stores/userStore';
import { prepareTenantHandoff } from '../../services/tenantHandoff';
import {
  configuredTenantOrigin,
  navigateExternal,
  tenantDirectLoginUrl,
} from '../../utils/unifiedSignIn';

/**
 * Shown instead of the landlord pages when a tenant-role session is open in the landlord app
 * (for example from an old bookmark). Friendly message rather than a screen of 403s.
 */
const TenantAccountNotice: React.FC = () => {
  const navigate = useNavigate();
  const [message, setMessage] = useState<string | null>(null);
  const tenantOrigin = configuredTenantOrigin();

  const openTenantPortal = async () => {
    const token = useUserStore.getState().token;
    if (!tenantOrigin || !token) {
      setMessage('We could not open the tenant portal from here. Please sign in again.');
      return;
    }
    const result = await prepareTenantHandoff(token, tenantOrigin);
    if (result.ok) {
      navigateExternal(result.url);
    } else {
      setMessage(result.message);
    }
  };

  const signOut = async () => {
    await useUserStore.getState().logout();
    navigate('/auth/signin', { replace: true });
  };

  return (
    <div className="flex min-h-screen items-center justify-center p-6">
      <div className="w-full max-w-md rounded-lg border border-stroke p-8 text-center dark:border-form-strokedark">
        <h1 className="mb-3 text-2xl font-bold text-black dark:text-white">This is a tenant account</h1>
        <p className="mb-6 text-gray-600 dark:text-gray-300">
          The landlord portal is for landlords and their staff. Your tenant pages are in the tenant portal.
        </p>
        {message && (
          <p role="alert" className="mb-4 text-sm text-red-700">
            {message}{' '}
            {tenantOrigin && (
              <a href={tenantDirectLoginUrl(tenantOrigin)} className="underline">
                Use the tenant sign-in
              </a>
            )}
          </p>
        )}
        <div className="flex flex-col gap-3">
          <button
            type="button"
            onClick={openTenantPortal}
            className="w-full rounded-lg border border-primary bg-primary p-3 text-white hover:bg-opacity-90"
          >
            Open the tenant portal
          </button>
          <button
            type="button"
            onClick={signOut}
            className="w-full rounded-lg border border-primary bg-transparent p-3 text-primary"
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
};

export default TenantAccountNotice;
